<template>
	<section v-if="src" :class="bannerClass">
		<picture class="game-banner__picture">
			<source v-if="resolvedPhoneBgImage" media="(max-width: 767px)" :srcset="resolvedPhoneBgImage">
			<img class="game-banner__image" :src="src" :alt="alt">
		</picture>
		<div v-if="showMobileWinnerMarquee" class="game-banner__mobile-winners">
			<div :class="winnerClass">
				<div v-if="resolvedWinnerTitle" :class="winnerTitleClass">{{ resolvedWinnerTitle }}</div>
				<div v-if="latestPrizeText" class="game-banner__winner-latest">{{ latestPrizeText }}</div>
				<div v-if="winnerMarqueeItems.length" class="game-banner__winner-marquee">
					<div :key="`mobile-winners-${winnerMarqueeVersion}`" class="game-banner__winner-track">
						<span v-for="(winner, index) in winnerMarqueeLoopItems" :key="index"
							class="game-banner__winner-item">{{ winner }}</span>
					</div>
				</div>
			</div>
		</div>

		<button v-if="hasDrawTrigger" class="game-banner__draw" type="button" :disabled="buttonDisabled"
			:aria-label="buttonAriaLabel" @click="showOrderWindow = true">
			<slot name="button">
				<img v-if="resolvedDrawImage" class="game-banner__draw-image" :src="resolvedDrawImage" alt="">
			</slot>
		</button>

		<div v-if="showActivityLayer" class="game-banner__activity">
			<div v-if="showFunctionalCountdown" class="game-banner__countdown">
				<slot name="countdown" :time="displayTime">
					<div :class="countdownArtClass">
						<img v-if="countdownImage" class="game-banner__countdown-image" :src="countdownImage" alt="">
						<span v-else :class="countdownLabelClass">{{ resolvedCountdownLabel }}</span>
						<div class="game-banner__countdown-values">
							<span>{{ displayTime.hours }}</span>
							<i>:</i>
							<span>{{ displayTime.minutes }}</span>
							<i>:</i>
							<span>{{ displayTime.seconds }}</span>
						</div>
					</div>
				</slot>
			</div>

			<button v-if="showFunctionalRule" class="game-banner__rule" type="button" :disabled="ruleDisabled"
				@click="showRuleModal = true">
				<slot name="rule">
					<span class="game-banner__rule-dot"></span>
					<span :class="ruleTextClass">{{ resolvedRuleText }}</span>
				</slot>
			</button>

			<div v-if="showWinnerList" class="game-banner__winner-wrap">
				<div v-if="showWinnerImage" class="game-banner__winner-image-box">
					<slot name="winner-image">
						<img class="game-banner__winner-image" :src="resolvedWinnerImage" alt="">
						<div class="game-banner__winner-image-table" :style="winnerImageTableStyle">
							<div v-for="(winner, index) in winnerImageTableItems" :key="index" :class="[
								'game-banner__winner-image-cell',
								{ 'game-banner__winner-image-cell--progress': winner.type === 'progress' }
							]">
								<div v-if="winner.type === 'progress'" class="game-banner__winner-progress-image-row">
									<div class="game-banner__winner-progress-stack">
										<div class="game-banner__winner-progress-bar-wrap">
											<img class="game-banner__winner-progress-image" :src="winner.progressImage" alt="">
											<div v-if="winner.progressGridImage && winner.progressGridItems.length"
												class="game-banner__winner-progress-grid-list">
												<img v-for="(_, gridIndex) in winner.progressGridItems" :key="gridIndex"
													class="game-banner__winner-progress-grid-image"
													:src="winner.progressGridImage" alt="">
											</div>
											<img v-if="winner.progressGridCount >= 10 && resolvedWinnerProgressStickerImage"
												:class="winnerProgressStickerClass"
												:src="resolvedWinnerProgressStickerImage"
												alt="">
										</div>
										<div v-if="winner.progressLeftImage || winner.progressRightImage"
											class="game-banner__winner-progress-label-images">
											<img v-if="winner.progressLeftImage" :src="winner.progressLeftImage" alt="">
											<img v-if="winner.progressRightImage" :src="winner.progressRightImage" alt="">
										</div>
									</div>
								</div>
								<template v-else>
									<img v-if="winner.image" class="game-banner__winner-image-cell-icon"
										:src="winner.image" alt="">
									<div class="game-banner__winner-image-cell-content">
										<div :class="winnerImageCellTitleClass">
											<template v-if="winner.titleParts && winner.titleParts.length">
												<span v-for="(part, partIndex) in winner.titleParts" :key="partIndex"
													:class="{ 'game-banner__winner-image-cell-title-highlight': part.highlight }">
													{{ part.text }}
												</span>
											</template>
											<template v-else>{{ winner.title }}</template>
										</div>
										<div v-if="winner.subtitle" :class="winnerImageCellSubtitleClass">
											{{ winner.subtitle }}
										</div>
									</div>
								</template>
							</div>
						</div>
					</slot>
				</div>
				<div :class="winnerClass">
					<slot name="winner">
						<div v-if="resolvedWinnerTitle" :class="winnerTitleClass">{{ resolvedWinnerTitle }}</div>
						<div v-if="latestPrizeText" class="game-banner__winner-latest">{{ latestPrizeText }}</div>
						<div v-if="winnerMarqueeItems.length" class="game-banner__winner-marquee">
							<div class="game-banner__winner-track">
								<span v-for="(winner, index) in winnerMarqueeLoopItems" :key="index"
									class="game-banner__winner-item">{{ winner }}</span>
							</div>
						</div>
					</slot>
				</div>
			</div>
		</div>
		<component :is="dynamicComponent" v-if="hasLogin && dynamicComponent && showOrderWindow" :goods="goods"
			:rebate="rebate" @close="closeOrderWindow">
		</component>

		<!-- 活動規則彈窗 -->
		<transition name="gb-rule-fade">
			<div v-if="showRuleModal" class="gb-rule-overlay" @click.self="showRuleModal = false">
				<div class="gb-rule-modal">
					<div class="gb-rule-modal__header">
						<div class="gb-rule-modal__title">{{ gameData && (gameData.gameCode === 'fzsgkr' || gameData.gameCode === 'lycqkr') ? '이벤트 규칙' : '活動規則' }}</div>
						<button class="gb-rule-modal__close" type="button" @click="showRuleModal = false">X</button>
					</div>
					<div v-if="gameData && gameData.gameCode === 'lycqkr'" class="gb-rule-modal__body">
						<p class="gb-rule-modal__intro">행운 추첨·쿠폰 대상</p>
						<p class="gb-rule-modal__intro">매일 공식 홈페이지에서 임의 금액을 충전하면, 당일 추첨에 자동 참여됩니다!</p>
						<p class="gb-rule-modal__intro">매일 오전 11:00에 당첨자가 발표되며, 행운의 용사님은 최대 [25% 페이백 쿠폰] 1장을 단독으로 획득할 수 있습니다!</p>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">1. 참여 자격</div>
							<div class="gb-rule-modal__section-body">
								<p>매일 00:00-23:59 기간 공식 홈페이지에서 임의 금액을 충전한 주공님은 당일 추첨 풀에 자동으로 포함됩니다.</p>
								<p>계정당 매일 첫 번째 충전 건만 인정되며, 같은 날 추가 충전은 중복 참여로 인정되지 않습니다.</p>
							</div>
						</div>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">2. 추첨 시간</div>
							<div class="gb-rule-modal__section-body">
								<p>매일 오전 10:00에 추첨을 진행하며, 행운의 주공님 1명을 선정합니다.</p>
								<p>추첨 대상은 전일 00:00-23:59기간 내 조건을 충족한 주공님입니다.</p>
							</div>
						</div>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">3. 추첨 조건 및 보상 안내</div>
							<div class="gb-rule-modal__section-body">
								<p>당일 추첨 풀 참여 인원이 이벤트 기준 최소 인원에 도달하면 [25% 페이백 쿠폰] ×1을 지급합니다.</p>
								<p>참여 인원이 최소 인원에 도달하지 못하더라도 추첨은 진행되며, 보상은 [10% 페이백 쿠폰] ×1로 조정됩니다.</p>
								<p>조건 달성 여부와 관계없이 매일 추첨은 반드시 진행되며, 보상은 절대 놓치지 않습니다!</p>
							</div>
						</div>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">4. 이벤트 보상</div>
							<div class="gb-rule-modal__section-body">
								<p class="gb-rule-modal__reward">25% 페이백 쿠폰: 기준 인원 달성 시 지급<br>10% 페이백 쿠폰: 기준 인원 미달성 시 보장 지급</p>
								<p>사용 방법: 공식 홈페이지 충전 시 페이백 쿠폰 사용을 선택하면, 해당 비율의 추가 페이백을 받을 수 있습니다.</p>
								<p>예시: 25% 쿠폰 사용 기준</p>
								<p>₩149000 금괴 상품 충전 시, 기본 149000 금괴는 충전 후 게임 내 즉시 지급되며, 추가 ₩37250 페이백 금괴는 우편으로 지급됩니다.</p>
								<p>유효 기간: 수령 후 24시간 이내 사용해야 합니다.</p>
								<p>적용 한도: 최대 ₩1000000 및 이하 충전 상품에만 사용할 수 있습니다.</p>
							</div>
						</div>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">5. 당첨자 확인</div>
							<div class="gb-rule-modal__section-body">
								<p>당첨된 용사님은 공식 홈페이지 로그인 후, [페이백 금괴] 메뉴에서 해당 쿠폰을 확인할 수 있습니다.</p>
								<p>공식 홈페이지 메인 [행운 추첨] 영역에는 전일 및 최근 당첨 용사님의 캐릭터명이 표시되며, 매일 추첨 후 갱신됩니다.</p>
							</div>
						</div>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">6. 유의사항</div>
							<div class="gb-rule-modal__section-body">
								<p>이벤트 내용은 내부 사정에 따라 변경될 수 있으며, 변경 시 공식 라운지에 공지를 통해 안내됩니다.</p>
								<p>본 이벤트는 기타 할인, 금괴 혜택과 중복 사용할 수 없습니다.</p>
								<p>부정 참여 또는 버그 악용이 확인될 경우, 추첨 및 당첨 자격이 취소될 수 있습니다.</p>
							</div>
						</div>
					</div>
					<div v-else-if="gameData && gameData.gameCode === 'fzsgkr'" class="gb-rule-modal__body">
						<p class="gb-rule-modal__intro">행운 추첨·쿠폰 대상</p>
						<p class="gb-rule-modal__intro">매일 공식 홈페이지에서 임의 금액을 충전하면, 당일 추첨에 자동 참여됩니다!</p>
						<p class="gb-rule-modal__intro">매일 오전 11:00에 당첨자가 발표되며, 행운의 주공님은 최대 [50% 페이백 쿠폰] 1장을 단독으로 획득할 수 있습니다!</p>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">1. 참여 자격</div>
							<div class="gb-rule-modal__section-body">
								<p>매일 00:00-23:59 기간 공식 홈페이지에서 임의 금액을 충전한 주공님은 당일 추첨 풀에 자동으로 포함됩니다.</p>
								<p>계정당 매일 첫 번째 충전 건만 인정되며, 같은 날 추가 충전은 중복 참여로 인정되지 않습니다.</p>
							</div>
						</div>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">2. 추첨 시간</div>
							<div class="gb-rule-modal__section-body">
								<p>매일 오전 10:00에 추첨을 진행하며, 행운의 주공님 1명을 선정합니다.</p>
								<p>추첨 대상은 전일 00:00-23:59기간 내 조건을 충족한 주공님입니다.</p>
							</div>
						</div>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">3. 추첨 조건 및 보상 안내</div>
							<div class="gb-rule-modal__section-body">
								<p>당일 추첨 풀 참여 인원이 이벤트 기준 최소 인원에 도달하면 [50% 페이백 쿠폰] ×1을 지급합니다.</p>
								<p>참여 인원이 최소 인원에 도달하지 못하더라도 추첨은 진행되며, 보상은 [30% 페이백 쿠폰] ×1로 조정됩니다.</p>
								<p>조건 달성 여부와 관계없이 매일 추첨은 반드시 진행되며, 보상은 절대 놓치지 않습니다!</p>
							</div>
						</div>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">4. 이벤트 보상</div>
							<div class="gb-rule-modal__section-body">
								<p class="gb-rule-modal__reward">50% 페이백 쿠폰: 기준 인원 달성 시 지급<br>30% 페이백 쿠폰: 기준 인원 미달성 시 보장 지급</p>
								<p>사용 방법: 공식 홈페이지 충전 시 페이백 쿠폰 사용을 선택하면, 해당 비율의 추가 페이백을 받을 수 있습니다.</p>
								<p>예시: 50% 쿠폰 사용 기준</p>
								<p>₩149000 상품 충전 시, 기본 149000 금전쿠폰는 충전 후 게임 내 즉시 지급되며, 추가 ₩74500 페이백 금전쿠폰은 고객센터 문의 후 우편으로 지급됩니다.</p>
								<p>유효 기간: 수령 후 24시간 이내 사용해야 합니다.</p>
								<p>적용 한도: 최대 ₩149000 및 이하 충전 상품에만 사용할 수 있습니다.</p>
							</div>
						</div>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">5. 당첨자 확인</div>
							<div class="gb-rule-modal__section-body">
								<p>당첨된 주공님은 공식 홈페이지 로그인 후, [페이백 금전쿠폰] 메뉴에서 해당 쿠폰을 확인할 수 있습니다.</p>
								<p>공식 홈페이지 메인 [행운 추첨] 영역에는 전일 및 최근 당첨 주공님의 캐릭터명이 표시되며, 매일 추첨 후 갱신됩니다.</p>
							</div>
						</div>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">6. 유의사항</div>
							<div class="gb-rule-modal__section-body">
								<p>이벤트 내용은 내부 사정에 따라 변경될 수 있으며, 변경 시 공식 라운지에 공지를 통해 안내됩니다.</p>
								<p>본 이벤트는 기타 할인, 금전 쿠폰 혜택과 중복 사용할 수 없습니다.</p>
								<p>부정 참여 또는 버그 악용이 확인될 경우, 추첨 및 당첨 자격이 취소될 수 있습니다.</p>
							</div>
						</div>
					</div>
					<div v-else class="gb-rule-modal__body">
						<p class="gb-rule-modal__intro">鴻運當頭抽大獎</p>
						<p class="gb-rule-modal__intro">每日於官網完成任意金額儲值，即可自動參加當日抽獎！</p>
						<p class="gb-rule-modal__intro">每日 上午 10:00 準時開獎，幸運兒最高將獨得【50% 返利券】1 張！</p>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">一、參與資格</div>
							<div class="gb-rule-modal__section-body">
								<p>每日 00:00 – 23:59 (UTC+8) 期間，於官網完成任意金額儲值的玩家，將自動進入當日獎池。</p>
								<p>每個帳號每日僅以第一筆儲值計算，同日的後續儲值不重複參加。</p>
							</div>
						</div>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">二、開獎時間</div>
							<div class="gb-rule-modal__section-body">
								<p>每日 上午 10:00 (UTC+8) 開獎，抽出 1 位幸運主公。</p>
								<p>抽獎範圍為前一日 00:00 – 23:59 符合資格的玩家。</p>
							</div>
						</div>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">三、開獎條件與獎勵等級（新機制）</div>
							<div class="gb-rule-modal__section-body">
								<p>當日獎池參與人數達到活動規定的最低人數門檻，開獎送出【50% 返利券】×1。</p>
								<p>當日獎池參與人數未達最低人數門檻，依然開獎，但獎勵調整為【30% 返利券】×1。</p>
								<p>無論是否達標，每日保證開獎，獎勵永不落空！</p>
							</div>
						</div>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">四、活動獎勵</div>
							<div class="gb-rule-modal__section-body">
								<p class="gb-rule-modal__reward">50% 返利券（達標時發放）／30% 返利券（未達標時保底發放）</p>
								<p>使用方式：在官網儲值時勾選使用返利券，即可獲得對應比例的額外回饋。</p>
								<p>範例（以 50% 券為例）：儲值 $49.99 品項，可獲得原 4,999 代金券（儲值後遊戲內立即發放）+ 2,499 返利代金券（聯繫客服郵件發放）。</p>
								<p>有效期限：領取後 24 小時內須使用完畢。</p>
								<p>適用上限：最高可用於 $49.99 及以下的儲值品項。</p>
							</div>
						</div>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">五、中獎展示</div>
							<div class="gb-rule-modal__section-body">
								<p>中獎玩家登入官網後，前往「返利券」專區即可看到對應返利券。</p>
								<p>官網首頁「鴻運當頭」專區將展示昨日及近期幸運兒（角色名），每日開獎後更新。</p>
							</div>
						</div>
						<div class="gb-rule-modal__section">
							<div class="gb-rule-modal__section-title">六、注意事項</div>
							<div class="gb-rule-modal__section-body">
								<p>官方保留活動最終修改與解釋權，任何調整將於官網公告。</p>
								<p>本活動不得與其他優惠碼、折扣或代金券同時使用。</p>
								<p>如發現違規或利用漏洞參加者，官方有權取消其抽獎及得獎資格。</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</transition>
	</section>
</template>

<script>
import axios from 'axios'
import { getGoodsJsonUrl, getRebateJsonUrl } from '@/utils/api/index.js'

const WINNER_CONFIG_BY_GAME = {
	sgyjltw: {
		topPhoneBgImage: 'https://cdn-download.dustyx.com/image/cms/20260922/95552fc6ce8c49428c6f4c9e928fa285.jpg',
		drawImage: '/static/image/banner/draw.png',
		countdownLabel: '距離今日開獎:',
		ruleText: '規則詳情',
		winnerTitle: '近期中獎名單',
		winnerProgressTitle: {
			prefix: '明日必定開獎',
			suffix: '返利券*1'
		},
		winnerImage: 'https://sea-pro-1317242925.cos.ap-singapore.myqcloud.com/act/2026/06/24/479eecfbe01c4fa7903434c1d0133556.png',
		winnerProgressStickerImage: 'https://sea-pro-1317242925.cos.ap-singapore.myqcloud.com/act/2026/06/25/7dab02c8d73842afb172eadc825b584e.png',
		winnerImageRows: [{
			title: '限每日儲值人數達標可開獎',
			image: 'https://sea-pro-1317242925.cos.ap-singapore.myqcloud.com/act/2026/06/24/871f4965707c4e6f8150ef7d6ace0671.png',
			progressImage: 'https://sea-pro-1317242925.cos.ap-singapore.myqcloud.com/act/2026/06/24/07cbbb4b72f4469cb5c8f787440ba64e.png',
			progressGridImage: 'https://sea-pro-1317242925.cos.ap-singapore.myqcloud.com/act/2026/06/24/e1902203127b4c9b977dca215d1c1b3c.png',
			progressGridCount: 10,
			progressLeftImage: 'https://sea-pro-1317242925.cos.ap-singapore.myqcloud.com/act/2026/06/24/d776b35121e04780a86e32b08551cd6c.png',
			progressRightImage: 'https://sea-pro-1317242925.cos.ap-singapore.myqcloud.com/act/2026/06/24/8a21044624c348d095708abede37d618.png'
		}, {
			title: '',
			subtitle: '每日抽取幸運玩家x1',
			image: 'https://sea-pro-1317242925.cos.ap-singapore.myqcloud.com/act/2026/06/24/fbae22aa8b424f1aa9f36b3ab52d75bf.png'
		}, {
			title: '每日10:00開獎',
			subtitle: '返利券獲得起24小時內有效',
			image: 'https://sea-pro-1317242925.cos.ap-singapore.myqcloud.com/act/2026/06/24/ed234a53ce94404e940d105a3b3ba823.png'
		}]
	},
	fzsgkr: {
		topPhoneBgImage: 'https://cdn-download.kodaduck.com/image/cms/20260721/e52a4671a3784f728615aa2df82abd97.png',
		drawImage: 'https://cdn-download.kodaduck.com/image/cms/20260724/9cc5b1191cec473da2fc9ca6c1a2352e.png',
		countdownLabel: '대상 추첨 남은 시간:',
		ruleText: '이벤트 규칙',
		winnerTitle: '최근 당첨자 명단',
		winnerProgressTitle: {
			prefix: '내일',
			suffix: '페이백 쿠폰 ×1 확정 추첨'
		},
		winnerImage: 'https://cdn-download.kodaduck.com/image/cms/20260724/fbebbf72d3bf479f88ee283dc90523e0.png',
		winnerProgressStickerImage: 'https://cdn-download.kodaduck.com/image/cms/20260724/9d2391c5a9a34d35800417c94f531d9a.png',
		winnerImageRows: [{
			title: '일일 충전 인원 목표 달성 시 추첨 진행',
			image: 'https://cdn-download.kodaduck.com/image/cms/20260724/7d30fe597e7e407291acb746005d3eb6.png',
			progressImage: 'https://cdn-download.kodaduck.com/image/cms/20260724/e19532b152d54885bd9012071cfeca0a.png',
			progressGridImage: 'https://cdn-download.kodaduck.com/image/cms/20260724/b24cf380c8b34b29b45d73d10dccfaed.png',
			progressGridCount: 10,
			progressLeftImage: 'https://cdn-download.kodaduck.com/image/cms/20260724/95847c810fb447f8877f279410d2057e.png',
			progressRightImage: 'https://cdn-download.kodaduck.com/image/cms/20260724/6ff5652cc32f4fa5aceabe9607bf24a1.png'
		}, {
			title: '',
			subtitle: '매일 행운의 주인공 1명 추첨',
			image: 'https://cdn-download.kodaduck.com/image/cms/20260724/5d850509527b4bfd8ef275950bb5402d.png'
		}, {
			title: '매일 11:00 당첨 발표',
			subtitle: '페이백 쿠폰은 획득 시점부터 24시간 동안 유효합니다',
			image: 'https://cdn-download.kodaduck.com/image/cms/20260724/a8c481569a284fbaab2645b78413361e.png'
		}]
	},
	lycqkr: {
		topPhoneBgImage: 'https://cdn-download.kodaduck.com/image/cms/20260922/11e4e809f6f1441fb2e00a7ad90d88d5.png',
		drawImage: 'https://cdn-download.kodaduck.com/image/cms/20260914/9d4f9de7f4ee42a29eb5f36fba40d22c.png',
		countdownLabel: '대상 추첨 남은 시간:',
		ruleText: '이벤트 규칙',
		winnerTitle: '최근 당첨자 명단',
		winnerProgressTitle: {
			prefix: '내일',
			suffix: '페이백 쿠폰 ×1 확정 추첨'
		},
		winnerImage: 'https://cdn-download.kodaduck.com/image/cms/20260914/3b6cfdcf0a814ac5a99355e4cd999674.png',
		winnerProgressStickerImage: 'https://cdn-download.kodaduck.com/image/cms/20260914/6e5c320b8d774cb88d7612849440b158.png',
		winnerImageRows: [{
			title: '일일 충전 인원 목표 달성 시 추첨 진행',
			image: 'https://cdn-download.kodaduck.com/image/cms/20260914/50a1c7fbd13541a7ad393052a53ca820.png',
			progressImage: 'https://cdn-download.kodaduck.com/image/cms/20260914/d920f21c25054163a6c37bac3c18151c.png',
			progressGridImage: 'https://cdn-download.kodaduck.com/image/cms/20260724/b24cf380c8b34b29b45d73d10dccfaed.png',
			progressGridCount: 10,
			progressLeftImage: 'https://cdn-download.kodaduck.com/image/cms/20260914/af0029872ace40b2b4a1c3d49642173b.png',
			progressRightImage: 'https://cdn-download.kodaduck.com/image/cms/20260914/27bc0a010c264c129fdd7528f63f0581.png'
		}, {
			title: '',
			subtitle: '매일 행운의 주인공 1명 추첨',
			image: 'https://cdn-download.kodaduck.com/image/cms/20260914/40284b4d4f0348e4ae47deb16e504163.png'
		}, {
			title: '매일 11:00 당첨 발표',
			subtitle: '페이백 쿠폰은 획득 시점부터 24시간 동안 유효합니다',
			image: 'https://cdn-download.kodaduck.com/image/cms/20260914/c53863e73b614fb2a61320a07ed75a80.png'
		}]
	}
};

export default {
	name: 'GameBanner',
	props: {
		src: {
			type: String,
			required: true
		},
		alt: {
			type: String,
			default: ''
		},
		variant: {
			type: String,
			default: 'normal',
			validator(value) {
				return ['normal', 'lottery', 'activity'].indexOf(value) > -1;
			}
		},
		buttonDisabled: {
			type: Boolean,
			default: false
		},
		buttonAriaLabel: {
			type: String,
			default: 'Open activity'
		},
		drawImage: {
			type: String,
			default: ''
		},
		drawProductId: {
			type: String,
			default: ''
		},
		countdownImage: {
			type: String,
			default: ''
		},
		countdownLabel: {
			type: String,
			default: ''
		},
		showCountdown: {
			type: Boolean,
			default: true
		},
		ruleText: {
			type: String,
			default: ''
		},
		showRule: {
			type: Boolean,
			default: true
		},
		ruleDisabled: {
			type: Boolean,
			default: false
		},
		winnerTitle: {
			type: String,
			default: ''
		},
		winnerProgressTitle: {
			type: Object,
			default: null
		},
		winners: {
			type: Array,
			default() {
				return [];
			}
		},
		winnerImage: {
			type: String,
			default: ''
		},
		winnerProgressStickerImage: {
			type: String,
			default: ''
		},
		winnerRowImage: {
			type: String,
			default: ''
		},
		winnerRowImages: {
			type: Array,
			default() {
				return [];
			}
		},
		winnerImageRows: {
			type: Array,
			default() {
				return [];
			}
		},
		winnerImageRowHeights: {
			type: Array,
			default() {
				return ['1.1fr', '1fr', '1.35fr', '1fr'];
			}
		},
		winnerImageRowIconSize: {
			type: String,
			default: '0.82rem'
		}
	},
	data() {
		return {
			now: Date.now(),
			timer: null,
			dynamicComponent: null,
			gameData: this.$store.state.gameData,
			hasLogin: this.$session.hasLogin(),
			roleInfo: this.$cache.getRoleInfo(),
			showOrderWindow: false,
			showRuleModal: false,
			goods: {},
			rebate: {},
			lotteryData: null,
			winnerMarqueeVersion: 0
		}
	},
	mounted() {
		this.syncTimer();
		if (this.hasLogin) {
			this.loadGoodsList();
			this.loadRebate();
			this.loadComponent();
		}
		if (this.variant === 'lottery') {
			this.loadLotteryInfo();
		}
	},
	computed: {
		bannerClass() {
			return [
				'game-banner',
				`game-banner--${this.variant}`,
				{ 'game-banner--has-mobile-winners': this.showMobileWinnerMarquee }
			];
		},
		isFunctional() {
			return this.variant === 'lottery' || this.variant === 'activity';
		},
		showActivityLayer() {
			return this.isFunctional;
		},
		hasDrawTrigger() {
			return this.isFunctional;
		},
		remainingSeconds() {
			return Math.max(0, Math.floor((this.nextTenAM - this.now) / 1000));
		},
		displayTime() {
			const seconds = this.remainingSeconds;
			const hours = Math.floor(seconds / 3600);
			const minutes = Math.floor((seconds % 3600) / 60);
			const restSeconds = seconds % 60;

			return {
				hours: this.padTime(hours),
				minutes: this.padTime(minutes),
				seconds: this.padTime(restSeconds)
			};
		},
		showFunctionalCountdown() {
			return this.showCountdown && this.isFunctional;
		},
		nextTenAM() {
			const now = new Date(this.now);
			const target = new Date(now);
			target.setHours(10, 0, 0, 0);
			if (now >= target) {
				target.setDate(target.getDate() + 1);
			}
			return target.getTime();
		},
		countdownArtClass() {
			return [
				'game-banner__countdown-art',
				{
					'game-banner__countdown-art--image': !!this.countdownImage
				}
			];
		},
		showFunctionalRule() {
			return this.showRule && (!!this.resolvedRuleText || !!this.$slots.rule);
		},
		resolvedCountdownLabel() {
			return this.countdownLabel || this.resolvedWinnerConfig.countdownLabel;
		},
		resolvedRuleText() {
			return this.ruleText || this.resolvedWinnerConfig.ruleText;
		},
		resolvedWinnerTitle() {
			return this.winnerTitle || this.resolvedWinnerConfig.winnerTitle;
		},
		isLottery() {
			return this.variant === 'lottery';
		},
		lotteryRecordNames() {
			if (!this.lotteryData || !Array.isArray(this.lotteryData.lotteryRecordList)) return [];
			return this.lotteryData.lotteryRecordList.map(function (record) {
				return record && (record.name || record.roleName || record.userName || record.nickname);
			}).filter(function (name) { return !!name; });
		},
		latestPrizeText() {
			if (!this.lotteryData || !this.lotteryData.prizeUser) return '';
			// 根据游戏编码切换昨日中奖标签语言
			const label = this.gameData
				&& (this.gameData.gameCode === 'fzsgkr' || this.gameData.gameCode === 'lycqkr')
				? '어제 당첨'
				: '昨日中獎';
			return `${label}:${this.lotteryData.prizeUser}`;
		},
		showWinnerList() {
			return !!this.$slots.winner || !!this.resolvedWinnerTitle || this.winners.length > 0
				|| (this.isLottery && this.lotteryRecordNames.length > 0);
		},
		showWinnerImage() {
			return !!this.$slots['winner-image'] || !!this.resolvedWinnerImage;
		},
		resolvedWinnerConfig() {
			const gameData = this.gameData || {};
			return WINNER_CONFIG_BY_GAME[gameData.gameCode] || {
				drawImage: '',
				countdownLabel: '',
				ruleText: '',
				winnerTitle: '',
				winnerProgressTitle: {
					prefix: '',
					suffix: ''
				},
				winnerImage: '',
				winnerProgressStickerImage: '',
				winnerImageRows: []
			};
		},
		resolvedPhoneBgImage() {
			// 手机端优先使用游戏数据中的图片，其次使用游戏专属配置
			return (this.gameData && this.gameData.topPhoneBgImage)
				|| this.resolvedWinnerConfig.topPhoneBgImage || '';
		},
		resolvedDrawImage() {
			return this.drawImage || this.resolvedWinnerConfig.drawImage;
		},
		resolvedWinnerImage() {
			return this.winnerImage || this.resolvedWinnerConfig.winnerImage;
		},
		resolvedWinnerProgressStickerImage() {
			return this.winnerProgressStickerImage
				|| this.resolvedWinnerConfig.winnerProgressStickerImage;
		},
		resolvedWinnerProgressTitle() {
			return this.winnerProgressTitle
				|| this.resolvedWinnerConfig.winnerProgressTitle;
		},
		resolvedWinnerImageRows() {
			return this.winnerImageRows.length
				? this.winnerImageRows
				: this.resolvedWinnerConfig.winnerImageRows;
		},
		winnerImageCellTitleClass() {
			const gameCode = (this.gameData && this.gameData.gameCode) || 'default';
			return [
				'game-banner__winner-image-cell-title',
				`game-banner__winner-image-cell-title--${gameCode}`
			];
		},
		winnerImageCellSubtitleClass() {
			const gameCode = (this.gameData && this.gameData.gameCode) || 'default';
			return [
				'game-banner__winner-image-cell-subtitle',
				`game-banner__winner-image-cell-subtitle--${gameCode}`
			];
		},
		winnerClass() {
			const gameCode = (this.gameData && this.gameData.gameCode) || 'default';
			return [
				'game-banner__winner',
				`game-banner__winner--${gameCode}`
			];
		},
		winnerTitleClass() {
			const gameCode = (this.gameData && this.gameData.gameCode) || 'default';
			return [
				'game-banner__winner-title',
				`game-banner__winner-title--${gameCode}`
			];
		},
		countdownLabelClass() {
			const gameCode = (this.gameData && this.gameData.gameCode) || 'default';
			return [
				'game-banner__countdown-label',
				`game-banner__countdown-label--${gameCode}`
			];
		},
		ruleTextClass() {
			const gameCode = (this.gameData && this.gameData.gameCode) || 'default';
			return [
				'game-banner__rule-text',
				`game-banner__rule-text--${gameCode}`
			];
		},
		winnerProgressStickerClass() {
			const gameCode = (this.gameData && this.gameData.gameCode) || 'default';
			return [
				'game-banner__winner-progress-sticker',
				`game-banner__winner-progress-sticker--${gameCode}`
			];
		},
		winnerMarqueeItems() {
			const items = this.isLottery && this.lotteryRecordNames.length
				? this.lotteryRecordNames
				: this.winners;
			return items.filter(function (item) { return !!item; });
		},
		winnerMarqueeLoopItems() {
			return this.winnerMarqueeItems.concat(this.winnerMarqueeItems);
		},
		showMobileWinnerMarquee() {
			return this.showActivityLayer && this.showWinnerList;
		},
		winnerImageTableStyle() {
			const rowHeights = this.winnerImageRowHeights.slice(0, 4);
			while (rowHeights.length < 4) {
				rowHeights.push('1fr');
			}
			return {
				gridTemplateRows: rowHeights.join(' '),
				'--winner-row-icon-size': this.winnerImageRowIconSize
			};
		},
		winnerImageTableItems() {
			const items = [];
			const fallbackProgressGridCount = Number((this.resolvedWinnerImageRows[0] && this.resolvedWinnerImageRows[0].progressGridCount) || 0);
			const hasApiUserCount = this.lotteryData && this.lotteryData.userCount !== undefined;
			const progressGridCount = hasApiUserCount
				? Number(this.lotteryData.userCount) || 0
				: fallbackProgressGridCount;
			this.resolvedWinnerImageRows.slice(0, 3).forEach((row, index) => {
				const isObject = row && typeof row === 'object';
				let title = isObject ? (row.title || row.text || '') : row;
				let titleParts = [];
				if (index === 1) {
					const progressTitle = this.resolvedWinnerProgressTitle;
					if (progressTitle.prefix || progressTitle.suffix) {
						// lycqkr 使用 10%/25% 档位，其他游戏保持 30%/50% 档位
						const isLycqkr = this.gameData && this.gameData.gameCode === 'lycqkr';
						const percentText = isLycqkr
							? (progressGridCount >= 10 ? '25%' : '10%')
							: (progressGridCount >= 10 ? '50%' : '30%');
						title = `${progressTitle.prefix}${percentText}${progressTitle.suffix}`;
						titleParts = [
							{ text: progressTitle.prefix, highlight: false },
							{ text: percentText, highlight: true },
							{ text: progressTitle.suffix, highlight: false }
						];
					} else {
						title = '';
					}
				}
				items.push({
					type: 'content',
					title: title,
					titleParts: titleParts,
					subtitle: isObject ? (row.subtitle || '') : '',
					image: isObject ? (row.image || this.winnerRowImages[index] || this.winnerRowImage) : (this.winnerRowImages[index] || this.winnerRowImage),
					progressImage: ''
				});
				if (isObject && row.progressImage) {
					items.push({
						type: 'progress',
						progressImage: row.progressImage,
						progressGridImage: row.progressGridImage || '',
						progressLeftImage: row.progressLeftImage || '',
						progressRightImage: row.progressRightImage || '',
						progressGridCount: progressGridCount,
						progressGridItems: Array.from({
							length: Math.max(0, Math.min(10, progressGridCount))
						})
					});
				}
			});
			while (items.length < 4) {
				items.push({
					type: 'content',
					title: '',
					subtitle: '',
					image: this.winnerRowImages[items.length] || this.winnerRowImage,
					progressImage: ''
				});
			}
			return items;
		}
	},
	watch: {
		variant: 'syncTimer'
	},
	beforeDestroy() {
		this.clearTimer();
	},
	methods: {
		padTime(value) {
			return String(value).padStart(2, '0');
		},
		syncTimer() {
			this.clearTimer();
			if (!this.isFunctional) return;

			this.now = Date.now();

			this.timer = window.setInterval(() => {
				this.now = Date.now();
			}, 1000);
		},
		clearTimer() {
			if (!this.timer) return;
			window.clearInterval(this.timer);
			this.timer = null;
		},
		loadGoodsList: function () {
			const _this = this;
			axios.get(getGoodsJsonUrl(this.gameData.gameCode))
				.then((response) => {
					const data = response.data || response;
					const allGoods = [
						...(data.directGoodsList || []),
						...(data.quotaGoodsList || [])
					];
					const target = _this.drawProductId
						? allGoods.find(g => g.productId === _this.drawProductId)
						: allGoods[0];
					_this.goods = target || {};
				});
		},
		loadRebate: function () {
			const _this = this;
			axios.get(getRebateJsonUrl(this.gameData.gameCode))
				.then((response) => {
					_this.rebate = response.data || response || {};
				});
		},
		closeOrderWindow: function () {
			this.showOrderWindow = false;
		},
		async loadComponent() {
			const {
				default: component
			} = await import('@/components/confirmOrder.vue');
			this.dynamicComponent = component;
		},
		loadLotteryInfo: function () {
			const userId = this.$session.getUserId();
			const token = this.$session.getLoginToken();
			const roleId = this.roleInfo ? this.roleInfo.roleId : '';
			// if (!userId || !roleId || !token) {
			// 	return;
			// }
			var _this = this;
			this.$axios({
				method: 'get',
				url: '/web/api/act/ActRechargePrizePoolInit',
				headers: {
					'X-Auth-Token': token
				},
				params: {
					userId,
					gameCode: this.$cache.getGameCode(),
					roleId,
					roleName: this.roleInfo ? this.roleInfo.roleName : ''
				}
			}).then(function (res) {
					if (res && res.code && res.code !== 1000) {
						return;
					}
					var data = (res && res.data) ? res.data : res;
					_this.lotteryData = data || null;
					_this.restartWinnerMarquee();
					if (_this.isAfterTodayTenAM()) {
						_this.loadRechargePrizePoolPrize(userId, token);
					}
				})
				.catch(function () { });
		},
		isAfterTodayTenAM: function () {
			const now = new Date();
			const todayTenAM = new Date(now);
			todayTenAM.setHours(10, 0, 0, 0);
			return now >= todayTenAM;
		},
		restartWinnerMarquee: function () {
			this.$nextTick(() => {
				this.winnerMarqueeVersion += 1;
				this.$nextTick(() => {
					const tracks = this.$el.querySelectorAll('.game-banner__mobile-winners .game-banner__winner-track');
					tracks.forEach((track) => {
						track.style.animation = 'none';
						void track.offsetWidth;
						track.style.animation = '';
					});
				});
			});
		},
		loadRechargePrizePoolPrize: function (userId, token) {
			if (!userId || !token) {
				return;
			}
			this.$axios({
				method: 'get',
				url: '/web/api/act/getRechargePrizePoolPrizeLimitOne',
				headers: {
					'X-Auth-Token': token
				},
				params: {
					userId,
					gameCode: this.$cache.getGameCode()
				}
			}).catch(function () { });
		}
	}
}
</script>

<style scoped>
.game-banner {
	position: relative;
	margin-top: 1rem;
	width: 24rem;
	height: 4.5rem;
	overflow: hidden;
}

.game-banner__picture {
	position: absolute;
	inset: 0;
	display: block;
}

.game-banner__image {
	display: block;
	width: 100%;
	height: 100%;
}

.game-banner__mobile-winners {
	display: none;
}

.game-banner--lottery {
	aspect-ratio: 1920 / 400;
	height: auto;
	min-height: 0;
	margin-bottom: 24px;
}

.game-banner--lottery .game-banner__winner-wrap {
	transform: scale(0.76);
	transform-origin: right bottom;
}

.game-banner--activity {
	aspect-ratio: 1920 / 600;
	height: auto;
	min-height: 7.5rem;
	margin-bottom: 0.6375rem;
}

.game-banner--lottery .game-banner__image {
	object-fit: cover;
}

.game-banner--activity .game-banner__image {
	object-fit: cover;
}

.game-banner__draw {
	position: absolute;
	left: 50%;
	top: 70%;
	width: 23%;
	height: 18%;
	z-index: 2;
	border: 0;
	padding: 0;
	background: transparent;
	cursor: pointer;
	transform: translate(-50%, -50%);
	overflow: hidden;
}

.game-banner--lottery .game-banner__draw {
	left: 50.5%;
	height: 22%;
}

.game-banner__draw:disabled {
	cursor: not-allowed;
	opacity: 0.65;
}

.game-banner__draw-image {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: contain;
}

.game-banner__activity {
	position: absolute;
	right: 0.35rem;
	bottom: 0.18rem;
	left: 35%;
	z-index: 3;
	display: flex;
	align-items: flex-end;
	justify-content: flex-start;
	gap: 0.22rem;
	pointer-events: none;
}

.game-banner--lottery .game-banner__activity {
	bottom: 0.1rem;
}

.game-banner__countdown {
	width: 5.75rem;
	pointer-events: auto;
}

.game-banner__countdown-art {
	position: relative;
	display: flex;
	align-items: center;
	gap: 0.14rem;
	width: 100%;
	min-height: 0.78rem;
	color: #fff5df;
	font-weight: 700;
}

.game-banner__countdown-art--image {
	display: block;
	min-height: 0;
}

.game-banner__countdown-art--image::before {
	content: none;
}

.game-banner__countdown-image {
	display: block;
	width: 100%;
	height: auto;
}

.game-banner__countdown-label {
	position: relative;
	flex: none;
	color: #fff8ed;
	font-size: 0.36rem;
	font-weight: 800;
	letter-spacing: 0;
	line-height: 0.78rem;
	text-shadow:
		0 0.018rem 0 #5d190b,
		0 0.04rem 0.04rem rgba(28, 7, 2, 0.95),
		0 0 0.1rem rgba(255, 156, 74, 0.5);
	-webkit-text-stroke: 0.006rem #4a1308;
}

.game-banner__countdown-label::after {
	content: '';
	position: absolute;
	right: 0.02rem;
	bottom: 0.1rem;
	left: 0.02rem;
	height: 0.03rem;
	border-radius: 0.03rem;
	background: linear-gradient(90deg, transparent, rgba(255, 211, 131, 0.9), transparent);
	opacity: 0.75;
}

.game-banner__countdown-values {
	display: grid;
	grid-template-columns: 0.78rem 0.16rem 0.78rem 0.16rem 0.78rem;
	align-items: center;
	gap: 0.12rem;
	width: auto;
}

.game-banner__countdown-art--image .game-banner__countdown-values {
	position: absolute;
	top: 0;
	right: 0.04rem;
	bottom: 0;
	width: 3.9rem;
}

.game-banner__countdown-values span {
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 0.78rem;
	height: 0.76rem;
	border: 0.015rem solid #d98f3d;
	border-radius: 0.1rem;
	background: #1d0703;
	color: #fff9ec;
	font-size: 0.5rem;
	font-weight: 700;
	letter-spacing: 0;
	line-height: 1;
	box-shadow:
		inset 0 0 0.04rem rgba(255, 200, 111, 0.16),
		0 0.02rem 0.04rem rgba(0, 0, 0, 0.35);
	text-shadow:
		0 0.018rem 0 #46170c,
		0 0 0.04rem rgba(39, 10, 4, 0.9);
	-webkit-text-stroke: 0.004rem #351209;
}

.game-banner__countdown-art--image .game-banner__countdown-values span {
	border-color: transparent;
	background: transparent;
	color: #fff8ea;
	font-size: 0.42rem;
	font-weight: 700;
	letter-spacing: 0;
	text-shadow:
		0 0.025rem 0 #351209,
		0 0 0.04rem rgba(39, 10, 4, 0.9);
	-webkit-text-stroke: 0.006rem #351209;
	transform: translateY(0.02rem);
}

.game-banner__countdown-values i {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.07rem;
	color: transparent;
	font-style: normal;
	font-size: 0;
	line-height: 1;
	text-align: center;
	animation: countdown-dot-blink 1s ease-in-out infinite;
}

@keyframes countdown-dot-blink {

	0%,
	100% {
		opacity: 1;
	}

	50% {
		opacity: 0.35;
	}
}

.game-banner__countdown-values i::before,
.game-banner__countdown-values i::after {
	content: '';
	display: block;
	width: 0.065rem;
	height: 0.065rem;
	border-radius: 50%;
	background: radial-gradient(circle at 35% 35%, #fffae8, #ffd87a);
	box-shadow:
		0 0 0.04rem rgba(255, 200, 80, 0.9),
		0 0 0.09rem rgba(255, 160, 40, 0.6);
}

.game-banner__countdown-values i::before {
	position: static;
	transform: none;
}

.game-banner__countdown-values i::after {
	position: static;
	transform: none;
}

.game-banner__rule {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.07rem;
	width: auto;
	min-height: 0.36rem;
	margin-bottom: 0.16rem;
	padding: 0;
	border: 0;
	background: transparent;
	color: #fff3df;
	cursor: pointer;
	font-size: 0.25rem;
	font-weight: 800;
	letter-spacing: 0;
	line-height: 1;
	pointer-events: auto;
	text-shadow:
		0 0.018rem 0 #4a1308,
		0 0 0.05rem rgba(42, 10, 4, 0.95);
	-webkit-text-stroke: 0.003rem #3b0e05;
}

.game-banner__rule-dot {
	display: inline-block;
	width: 0.16rem;
	height: 0.16rem;
	border: 0.035rem solid #fff3df;
	border-radius: 50%;
	box-shadow:
		0 0 0.02rem #4a1308,
		0 0 0.06rem rgba(255, 171, 82, 0.45);
	box-sizing: border-box;
}

.game-banner__rule-text {
	display: inline-block;
	white-space: nowrap;
}

.game-banner__rule:disabled {
	cursor: not-allowed;
	opacity: 0.65;
}

.game-banner__winner-wrap {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	margin-left: auto;
	width: 7.25rem;
	pointer-events: auto;
}

.game-banner__winner-image-box {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	aspect-ratio: 414 / 273;
	margin-bottom: 0.08rem;
}

.game-banner__winner-image {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: contain;
}

.game-banner__winner-image-table {
	position: absolute;
	top: 5%;
	right: 7%;
	bottom: 5%;
	left: 7%;
	display: grid;
	align-items: stretch;
	pointer-events: none;
}

.game-banner__winner-image-cell {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 0.18rem;
	min-width: 0;
	padding: 0.06rem 0.12rem;
	overflow: hidden;
	color: #8b4f46;
	line-height: 1.1;
	text-align: left;
	box-sizing: border-box;
}

.game-banner__winner-image-cell--progress {
	justify-content: center;
	gap: 0;
	padding: 0.02rem 0.34rem;
	overflow: visible;
}

.game-banner__winner-image-cell-icon {
	flex: 0 0 auto;
	width: var(--winner-row-icon-size, 0.82rem);
	height: var(--winner-row-icon-size, 0.82rem);
	object-fit: contain;
}

.game-banner__winner-image-cell-content {
	flex: 1 1 auto;
	min-width: 0;
}

.game-banner__winner-image-cell-title {
	font-family: SourceHanSerifCN, SourceHanSerifCN;
	font-weight: 800;
	font-size: 0.38rem;
	color: #793922;
	text-align: left;
	font-style: normal;
	text-transform: none;
}

.game-banner__winner-image-cell-title-highlight {
	color: #FDDC87;
	-webkit-text-stroke: 0.006rem #8b3f0c;
	text-shadow:
		0.022rem 0 0.01rem #8b3f0c,
		-0.022rem 0 0.01rem #8b3f0c,
		0 0.022rem 0.01rem #8b3f0c,
		0 -0.022rem 0.01rem #8b3f0c,
		0.018rem 0.03rem 0.018rem rgba(112, 47, 4, 0.82);
}

.game-banner__winner-image-cell-subtitle {
	margin-top: 0.04rem;
	overflow: hidden;
	color: #8d5d52;
	font-size: 0.16rem;
	font-weight: 700;
	line-height: 1.2;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.game-banner__winner-progress-image-row {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
}

.game-banner__winner-progress-stack {
	width: 100%;
}

.game-banner__winner-progress-bar-wrap {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
}

.game-banner__winner-progress-image {
	display: block;
	width: 110%;
	max-width: none;
	height: auto;
	object-fit: contain;
}

.game-banner__winner-progress-grid-list {
	position: absolute;
	top: 50%;
	left: 0.45rem;
	display: flex;
	align-items: center;
	width: calc(100% - clamp(0px, 1.5vw, 28px));
	pointer-events: none;
	transform: translateY(-50%);
}

.game-banner__winner-progress-grid-image {
	display: block;
	flex: 0 0 auto;
	width: 9%;
	height: auto;
	object-fit: contain;
}

.game-banner__winner-progress-sticker {
	position: absolute;
	top: 50%;
	right: -0.6rem;
	z-index: 10;
	display: block;
	width: 1.38rem;
	height: auto;
	object-fit: contain;
	pointer-events: none;
	transform: translateY(-50%) rotate(-20deg);
}

.game-banner__winner-progress-label-images {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	margin-top: 0.02rem;
}

.game-banner__winner-progress-label-images img {
	display: block;
	width: 0.9rem;
	height: auto;
	object-fit: contain;
}

.game-banner__winner {
	width: 100%;
	min-height: 1.1rem;
	overflow: hidden;
	border: 0.018rem solid rgba(244, 146, 55, 0.86);
	border-radius: 0.1rem;
	background:
		linear-gradient(180deg, rgba(133, 25, 20, 0.96) 0%, rgba(104, 23, 17, 0.94) 0.44rem, rgba(58, 12, 8, 0.9) 100%),
		radial-gradient(circle at 82% 16%, rgba(255, 169, 71, 0.24), transparent 44%);
	box-shadow:
		inset 0 0 0.22rem rgba(255, 113, 43, 0.16),
		0 0.04rem 0.18rem rgba(23, 4, 2, 0.28);
	color: #ffe9c4;
	pointer-events: auto;
}

.game-banner__winner-title {
	height: 0.44rem;
	color: #fff2cf;
	background: linear-gradient(180deg, rgba(150, 35, 29, 0.94) 0%, rgba(124, 25, 21, 0.92) 100%);
	font-size: 0.28rem;
	font-weight: 800;
	letter-spacing: 0;
	line-height: 0.44rem;
	text-align: center;
	text-shadow: 0 0.018rem 0 #4b0d07, 0 0 0.08rem rgba(255, 177, 79, 0.42);
}

.game-banner__winner-latest {
	height: 0.32rem;
	padding: 0 0.24rem;
	overflow: hidden;
	color: #ffe7bd;
	font-size: 0.18rem;
	font-weight: 700;
	line-height: 0.32rem;
	text-align: center;
	text-overflow: ellipsis;
	text-shadow: 0 0.012rem 0 #3d0d07, 0 0 0.06rem rgba(255, 176, 78, 0.25);
	white-space: nowrap;
	box-sizing: border-box;
}

.game-banner__winner-marquee {
	position: relative;
	height: 0.32rem;
	overflow: hidden;
	padding: 0 0.24rem;
	mask-image: linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%);
}

.game-banner__winner-track {
	display: inline-flex;
	align-items: center;
	min-width: max-content;
	height: 100%;
	white-space: nowrap;
	animation: winner-marquee 18s linear infinite;
	will-change: transform;
}

.game-banner__winner:hover .game-banner__winner-track {
	animation-play-state: paused;
}

.game-banner__winner-item {
	display: inline-flex;
	align-items: center;
	flex: 0 0 auto;
	margin-right: 0.56rem;
	color: #ffe7bd;
	font-size: 0.19rem;
	font-weight: 700;
	line-height: 0.32rem;
	text-shadow: 0 0.012rem 0 #3d0d07, 0 0 0.06rem rgba(255, 176, 78, 0.25);
}

.game-banner__winner-item::before {
	content: '';
	display: inline-block;
	width: 0.06rem;
	height: 0.06rem;
	margin-right: 0.12rem;
	border-radius: 50%;
	background: #ffb84b;
	box-shadow: 0 0 0.08rem rgba(255, 179, 72, 0.85);
}

@keyframes winner-marquee {
	0% {
		transform: translateX(0);
	}

	100% {
		transform: translateX(-50%);
	}
}

@media (max-width: 767px) {
	.game-banner {
		margin-top: 1.1733rem;
		width: 100%;
		height: 2.32rem;
	}

	.game-banner--lottery {
		aspect-ratio: 1920 / 400;
		height: auto;
		min-height: 4.4rem;
		margin-bottom: 0.6375rem;
	}

	.game-banner--activity {
		aspect-ratio: 1920 / 600;
		height: auto;
		min-height: 3.125rem;
		margin-bottom: 0.6375rem;
	}

	.game-banner__activity {
		right: 0.12rem;
		bottom: 0.08rem;
		gap: 0.08rem;
	}

	.game-banner--lottery .game-banner__activity {
		bottom: 0.08rem;
	}

	.game-banner__countdown {
		width: 3.05rem;
	}

	.game-banner--lottery .game-banner__countdown {
		flex: none;
		width: 4.5rem;
	}

	.game-banner--lottery .game-banner__countdown-art {
		/* 按手机端 10rem 的 Banner 宽度，将放大后的倒计时置于中线 */
		width: 83.333%;
		justify-content: center;
		transform: translateX(-1.3rem) scale(1.5);
		transform-origin: left bottom;
	}

	.game-banner__countdown-art {
		gap: 0.07rem;
		min-height: 0.44rem;
	}

	.game-banner__countdown-label {
		font-size: 0.18rem;
		line-height: 0.42rem;
	}

	.game-banner__countdown-label::after {
		bottom: 0.04rem;
		height: 0.015rem;
	}

	.game-banner__countdown-values {
		grid-template-columns: 0.4rem 0.07rem 0.4rem 0.07rem 0.4rem;
		gap: 0.05rem;
	}

	.game-banner__countdown-art--image .game-banner__countdown-values {
		top: 0;
		right: 0.02rem;
		bottom: 0;
		width: 1.95rem;
	}

	.game-banner__countdown-values span {
		min-width: 0.4rem;
		height: 0.39rem;
		border-radius: 0.05rem;
		font-size: 0.2rem;
	}

	.game-banner__countdown-values i {
		gap: 0.07rem;
	}

	.game-banner__countdown-values i::before,
	.game-banner__countdown-values i::after {
		width: 0.034rem;
		height: 0.034rem;
	}

	.game-banner__rule {
		gap: 0.035rem;
		width: auto;
		min-height: 0.18rem;
		margin-bottom: 0.1rem;
		font-size: 0.12rem;
	}

	.game-banner__rule-dot {
		width: 0.085rem;
		height: 0.085rem;
		border-width: 0.018rem;
	}

	.game-banner__winner-wrap {
		display: none;
	}

	.game-banner--has-mobile-winners {
		margin-bottom: 2.35rem;
		overflow: visible;
	}

	.game-banner__mobile-winners {
		position: absolute;
		top: 100%;
		left: 50%;
		z-index: 4;
		display: block;
		width: 100%;
		transform: translateX(-50%);
	}

	.game-banner__mobile-winners .game-banner__winner-marquee {
		display: flex;
		align-items: center;
		width: 100%;
		box-sizing: border-box;
	}

	.game-banner__mobile-winners .game-banner__winner-track {
		display: flex;
		align-items: center;
		flex: 0 0 auto;
		margin: 0;
	}
}

/* =================== Rule Modal =================== */
.gb-rule-overlay {
	position: fixed;
	inset: 0;
	z-index: 9999;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0.4rem;
	background: rgba(0, 0, 0, 0.7);
	box-sizing: border-box;
}

.gb-rule-modal {
	position: relative;
	display: flex;
	flex-direction: column;
	width: 100%;
	max-width: 16.5rem;
	max-height: 76vh;
	border-radius: 0.16rem;
	border: 0.015rem solid rgba(0, 0, 0, 0.1);
	background: #fff;
	overflow: hidden;
	box-shadow: 0 0.3rem 1.2rem rgba(0, 0, 0, 0.35), 0 0 0.5rem color-mix(in srgb, var(--theme-color) 20%, transparent);
}

.gb-rule-modal__header {
	flex: none;
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0.2rem 0.8rem;
	background: linear-gradient(135deg, color-mix(in srgb, var(--theme-color) 88%, #000) 0%, var(--theme-color) 50%, color-mix(in srgb, var(--theme-color) 88%, #000) 100%);
}

.gb-rule-modal__title {
	font-size: 0.3rem;
	font-weight: 700;
	color: #fff;
	letter-spacing: 0.01rem;
}

.gb-rule-modal__close {
	position: absolute;
	top: 50%;
	right: 0.24rem;
	transform: translateY(-50%);
	display: flex;
	align-items: center;
	justify-content: center;
	width: 0.42rem;
	height: 0.42rem;
	border: none;
	border-radius: 50%;
	background: rgba(0, 0, 0, 0.2);
	color: #fff;
	font-size: 0.18rem;
	font-weight: 700;
	cursor: pointer;
	transition: background 0.2s;
	line-height: 1;
}

.gb-rule-modal__close:hover {
	background: rgba(255, 255, 255, 0.35);
}

.gb-rule-modal__body {
	flex: 1;
	overflow-y: auto;
	padding: 0.24rem 0.36rem;
	color: #333;
	font-size: 0.22rem;
	line-height: 1.85;
	scrollbar-width: thin;
	scrollbar-color: color-mix(in srgb, var(--theme-color) 35%, transparent) transparent;
}

.gb-rule-modal__body::-webkit-scrollbar {
	width: 0.08rem;
}

.gb-rule-modal__body::-webkit-scrollbar-track {
	background: transparent;
}

.gb-rule-modal__body::-webkit-scrollbar-thumb {
	background: color-mix(in srgb, var(--theme-color) 35%, transparent);
	border-radius: 0.04rem;
}

.gb-rule-modal__intro {
	margin-bottom: 0.22rem;
	padding-bottom: 0.18rem;
	border-bottom: 0.01rem solid rgba(0, 0, 0, 0.08);
	color: #444;
	font-size: 0.22rem;
	line-height: 1.75;
}

.gb-rule-modal__highlight {
	color: var(--theme-color);
	font-weight: 700;
}

.gb-rule-modal__section {
	margin-bottom: 0.2rem;
}

.gb-rule-modal__section:last-child {
	margin-bottom: 0;
}

.gb-rule-modal__section-title {
	font-size: 0.23rem;
	font-weight: 700;
	color: var(--theme-color);
	margin-bottom: 0.08rem;
	padding-left: 0.15rem;
	border-left: 0.05rem solid var(--theme-color);
	line-height: 1.4;
}

.gb-rule-modal__section-body {
	padding-left: 0.2rem;
	font-size: 0.21rem;
	line-height: 1.8;
	color: #555;
}

.gb-rule-modal__section-body p {
	margin: 0 0 0.07rem;
}

.gb-rule-modal__section-body p:last-child {
	margin: 0;
}

.gb-rule-modal__reward {
	display: inline-block;
	margin-bottom: 0.08rem;
	padding: 0.05rem 0.18rem;
	border-radius: 0.05rem;
	background: color-mix(in srgb, var(--theme-color) 15%, transparent);
	border: 0.01rem solid color-mix(in srgb, var(--theme-color) 45%, transparent);
	font-size: 0.22rem;
	font-weight: 700;
	color: var(--theme-color);
}

/* Transition */
.gb-rule-fade-enter-active {
	transition: opacity 0.25s ease;
}

.gb-rule-fade-leave-active {
	transition: opacity 0.2s ease;
}

.gb-rule-fade-enter-active .gb-rule-modal {
	transition: opacity 0.25s ease, transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}

.gb-rule-fade-leave-active .gb-rule-modal {
	transition: opacity 0.2s ease, transform 0.2s ease;
}

.gb-rule-fade-enter,
.gb-rule-fade-leave-to {
	opacity: 0;
}

.gb-rule-fade-enter .gb-rule-modal {
	opacity: 0;
	transform: scale(0.95) translateY(0.3rem);
}

.gb-rule-fade-leave-to .gb-rule-modal {
	opacity: 0;
}

@media (max-width: 767px) {
	.gb-rule-overlay {
		padding: 0.25rem;
	}

	.gb-rule-modal {
		max-width: 100%;
		max-height: 88vh;
	}

	.gb-rule-modal__body {
		padding: 0.28rem 0.32rem;
	}
}






.game-banner__winner-image-cell-title--fzsgkr {
	font-family: "Malgun Gothic", sans-serif;
	font-weight: 800;
	font-size: 0.38rem;
	color: #FAD897;
	text-align: left;
	font-style: normal;
	text-transform: none;
}

.game-banner__winner-image-cell-subtitle--fzsgkr {
	font-family: "Malgun Gothic", sans-serif;
	margin-top: 0.04rem;
	overflow: hidden;
	color: #AF84AF;
	font-size: 0.16rem;
	font-weight: 700;
	line-height: 1.2;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.game-banner__winner--fzsgkr {
	border-color: rgba(224, 83, 211, 0.92);
	background:
		linear-gradient(180deg, rgba(80, 25, 89, 0.97) 0%, rgba(69, 19, 79, 0.96) 55%, rgba(57, 14, 68, 0.96) 100%),
		radial-gradient(circle at 82% 18%, rgba(198, 83, 206, 0.2), transparent 46%);
	box-shadow:
		inset 0 0 0.22rem rgba(206, 83, 211, 0.14),
		0 0.04rem 0.18rem rgba(25, 2, 31, 0.36);
	color: #f7dca6;
}

.game-banner__winner-title--fzsgkr {
	border-bottom: 0.01rem solid rgba(126, 46, 139, 0.62);
	color: #ffe2a2;
	background: linear-gradient(180deg, rgba(52, 12, 64, 0.98) 0%, rgba(42, 8, 54, 0.97) 100%);
	font-family: "Malgun Gothic", sans-serif;
	text-shadow:
		0 0.018rem 0 #35103d,
		0 0 0.07rem rgba(255, 211, 127, 0.34);
}
.game-banner__countdown-label--fzsgkr {
	position: relative;
	flex: none;
	color: #fff8ed;
	font-size: 0.28rem;
	font-weight: 800;
	letter-spacing: 0;
	line-height: 0.78rem;
	text-shadow:
		0 0.018rem 0 #5d190b,
		0 0.04rem 0.04rem rgba(28, 7, 2, 0.95),
		0 0 0.1rem rgba(255, 156, 74, 0.5);
	-webkit-text-stroke: 0.006rem #4a1308;
}
.game-banner__rule-text--fzsgkr {
	display: inline-block;
	white-space: nowrap;
	font-size: 0.18rem;
	margin-top: 0.05rem;
}

.game-banner__winner-progress-sticker--fzsgkr {
	position: absolute;
	top: 50%;
	right: -0.6rem;
	z-index: 10;
	display: block;
	width: 1.18rem;
	height: auto;
	object-fit: contain;
	pointer-events: none;
	transform: translateY(-50%) rotate(-20deg);
}

/* lycqkr 游戏专属样式 */
.game-banner__winner-image-cell-title--lycqkr {
	font-family: "Malgun Gothic", sans-serif;
	font-weight: 800;
	font-size: 0.38rem;
	color: #d92b0b;
	text-align: left;
	font-style: normal;
	text-transform: none;
}

.game-banner__winner-image-cell-title--lycqkr .game-banner__winner-image-cell-title-highlight {
	color: inherit;
	-webkit-text-stroke: 0;
	text-shadow: none;
}

.game-banner__winner-image-cell-subtitle--lycqkr {
	font-family: "Malgun Gothic", sans-serif;
	margin-top: 0.04rem;
	overflow: hidden;
	color: #d85a3a;
	font-size: 0.16rem;
	font-weight: 700;
	line-height: 1.2;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.game-banner__winner--lycqkr {
	border-color: rgba(205, 124, 11, 0.95);
	background: linear-gradient(105deg, #ffe38a 0%, #fff2bd 38%, #fffbed 100%);
	box-shadow:
		inset 0 0 0 0.015rem rgba(255, 251, 221, 0.95),
		inset 0 0 0.22rem rgba(216, 145, 16, 0.2),
		0 0.04rem 0.18rem rgba(121, 63, 3, 0.24);
	color: #d92b0b;
}

.game-banner__winner-title--lycqkr {
	border-bottom: 0.01rem solid rgba(205, 124, 11, 0.48);
	color: #d92b0b;
	background: linear-gradient(180deg, rgba(255, 226, 132, 0.96) 0%, rgba(255, 241, 187, 0.94) 100%);
	font-family: "Malgun Gothic", sans-serif;
	text-shadow: 0 0.01rem 0 rgba(255, 249, 218, 0.72);
}

.game-banner__winner--lycqkr .game-banner__winner-latest,
.game-banner__winner--lycqkr .game-banner__winner-item {
	color: #b93618;
	text-shadow: none;
}

.game-banner__winner--lycqkr .game-banner__winner-item::before {
	background: #d88b14;
	box-shadow: 0 0 0.07rem rgba(205, 124, 11, 0.58);
}

.game-banner__countdown-label--lycqkr {
	position: relative;
	flex: none;
	color: #fff8ed;
	font-size: 0.28rem;
	font-weight: 800;
	letter-spacing: 0;
	line-height: 0.78rem;
	text-shadow:
		0 0.018rem 0 #5d190b,
		0 0.04rem 0.04rem rgba(28, 7, 2, 0.95),
		0 0 0.1rem rgba(255, 156, 74, 0.5);
	-webkit-text-stroke: 0.006rem #4a1308;
}

.game-banner__rule-text--lycqkr {
	display: inline-block;
	white-space: nowrap;
	font-size: 0.18rem;
	margin-top: 0.05rem;
}

.game-banner__winner-progress-sticker--lycqkr {
	position: absolute;
	top: 50%;
	right: -0.6rem;
	z-index: 10;
	display: block;
	width: 1.18rem;
	height: auto;
	object-fit: contain;
	pointer-events: none;
	transform: translateY(-50%) rotate(-20deg);
}

@media (max-width: 767px) {
	.game-banner__countdown-art {
		gap: 0.05rem;
	}

	.game-banner__countdown-label {
		min-width: 0;
		font-size: 0.18rem;
		line-height: 0.42rem;
		white-space: nowrap;
	}

	.game-banner__countdown-label--fzsgkr {
		flex: 0 1 1.3rem;
		max-width: 1.3rem;
		font-size: 0.15rem;
		line-height: 1.15;
		text-align: center;
		white-space: normal;
		word-break: keep-all;
		-webkit-text-stroke-width: 0.003rem;
	}

	.game-banner__countdown-label--fzsgkr::after {
		bottom: -0.04rem;
	}

	.game-banner__countdown-values {
		flex: none;
	}

	.game-banner__rule {
		min-height: 0.28rem;
		margin-bottom: 0.06rem;
		padding: 0.03rem 0.02rem;
	}

	.game-banner__rule-text,
	.game-banner__rule-text--fzsgkr {
		margin-top: 0;
		font-size: 0.12rem;
		line-height: 1.2;
	}

	.game-banner__countdown-label--lycqkr {
		flex: none;
		font-size: 0.15rem;
		line-height: 1.15;
		text-align: center;
		white-space: nowrap;
		-webkit-text-stroke-width: 0.003rem;
	}

	.game-banner__countdown-label--lycqkr::after {
		bottom: -0.04rem;
	}

	.game-banner__rule-text--lycqkr {
		margin-top: 0;
		font-size: 0.12rem;
		line-height: 1.2;
	}

	.game-banner--lottery .game-banner__countdown-label {
		flex: none;
		max-width: none;
		font-size: 0.18rem;
		white-space: nowrap;
	}
}
</style>
